import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOnScreen } from "../../../domains/app/hooks/useOnScreen";
import { canDisplayArticleContent } from "../utils/contentVisibility";
import type { ArticlePageArticle } from "./useArticleData";
import type { WorksScoreSheetTarget } from "../components/sections/ArticleSectionWorksScore";

export type ArticlePageTabName = "overview" | "abstract" | "source";

export function useArticlePageUIState(params: {
  slug: string;
  article: ArticlePageArticle | null;
  /**
   * The carrying room's own visibility: `true` public, `false` private, `null`
   * when there is no room, or when the room is not known yet.
   *
   * `null` is not a transient. A shared `/room/post/...` link opened without a
   * session never learns the answer: the room row sits behind `guardRoom`, and
   * a reader with no membership cannot have it.
   */
  roomIsPublic: boolean | null;
}) {
  const WEBAPP_SHOW_NC_LICENSE = useFeatureIsOn("WEBAPP_SHOW_NC_LICENSE");
  const derived = useMemo(() => {
    const hasTldrText =
      params.article !== null &&
      params.article.tldr !== null &&
      params.article.tldr.length > 0;
    const hasImages =
      params.article !== null && params.article.images.length > 0;
    const canDisplayContent = canDisplayArticleContent({
      doi: params.article === null ? null : params.article.doi,
      license: params.article === null ? null : params.article.license,
      isOpenAccess:
        params.article === null ? null : params.article.isOpenAccess,
      // The gate LIFTS for a private room, because the Elsevier terms make that
      // exception for a client's own audience. Only a room we know is private
      // may claim it, so `null` counts as public and the gate stays on. The
      // `?.` this replaces read `null` the other way, and an anonymous visitor
      // holding a shared link got the digest that a signed-in reader of the
      // same paper does not.
      isPublic: params.roomIsPublic !== false,
      showNcLicense: WEBAPP_SHOW_NC_LICENSE,
    });
    const hasTldr = hasTldrText || hasImages;
    const isAllowedArticle =
      params.article?.isOpenAccess === true ||
      (params.article?.meta.creationDate !== null &&
        params.article?.meta.creationDate !== undefined &&
        new Date(params.article.meta.creationDate) >= new Date("2025-01-14"));
    const hasDigestContent =
      (params.article !== null &&
        params.article.background !== null &&
        params.article.background.length > 0) ||
      (params.article?.objectives.some((el) => el !== "") ?? false) ||
      (params.article !== null &&
        params.article.methodology !== null &&
        params.article.methodology.length > 0) ||
      (params.article?.results.some((el) => el !== "") ?? false) ||
      (params.article?.conclusion.some((el) => el !== "") ?? false) ||
      (params.article !== null &&
        params.article.limitations !== null &&
        params.article.limitations.length > 0);

    // Show abstractText when it comes from curated metadata (PubMed/CrossRef).
    // For pre-2025 closed-access articles (isAllowedArticle=false), also accept
    // null/unknown since those predate abstractSource tracking.
    // NOTE: ~6 existing allowed articles with abstractText + no IMRAD +
    // abstractSource=null will lose their abstract tab until backfilled.
    // Accepted trade-off — they still have Overview (TL;DR) + Source.
    const abstractSource =
      params.article !== null ? params.article.abstractSource : null;
    const hasAbstractText =
      params.article !== null &&
      params.article.abstractText !== null &&
      params.article.abstractText.length > 0;
    const hasMetadataAbstract =
      hasAbstractText && abstractSource === "metadata";
    const hasLegacyAbstract =
      !isAllowedArticle && hasAbstractText && abstractSource !== "pdf";
    const hasShowableAbstract = hasMetadataAbstract || hasLegacyAbstract;

    const tabs: ArticlePageTabName[] = [];

    if ((hasTldr || hasImages) && isAllowedArticle) {
      tabs.push("overview");
    }

    // Abstract tab: IMRAD sections when available, or abstractText when
    // sourced from metadata (or legacy null/unknown for pre-2025 articles)
    if (
      (canDisplayContent && isAllowedArticle && hasDigestContent) ||
      hasShowableAbstract
    ) {
      tabs.push("abstract");
    }

    tabs.push("source");

    return {
      enabledTabs: tabs,
      canDisplayContent,
      isAllowedArticle,
      hasDigestContent,
      hasShowableAbstract,
    };
  }, [WEBAPP_SHOW_NC_LICENSE, params.article, params.roomIsPublic]);
  const {
    enabledTabs,
    canDisplayContent,
    isAllowedArticle,
    hasDigestContent,
    hasShowableAbstract,
  } = derived;
  const [activeTab, setActiveTab] = useState<ArticlePageTabName>(
    enabledTabs.at(0) ?? "overview"
  );
  const [isBackgroundExpanded, setBackgroundExpanded] = useState(false);
  const [isMethodologyExpanded, setMethodologyExpanded] = useState(false);
  const [isLimitationsExpanded, setLimitationsExpanded] = useState(false);
  const [isObjectivesExpanded, setObjectivesExpanded] = useState(false);
  const [isResultsExpanded, setResultsExpanded] = useState(false);
  const [isConclusionExpanded, setConclusionExpanded] = useState(false);
  const [isPlaylistsPanelVisible, setPlaylistsPanelVisible] = useState(false);
  const [worksScoreSheetTarget, setWorksScoreSheetTarget] =
    useState<WorksScoreSheetTarget>("closed");
  const {
    isIntersecting: isArticleReadMarkerOnScreen,
    ref: articleReadMarkerRef,
  } = useOnScreen();
  const contentTopRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const firstTab = enabledTabs.at(0);
    if (firstTab !== undefined) {
      setActiveTab(firstTab);
    }
  }, [params.slug, enabledTabs]);

  // The evidence sheet must not follow the reader to the next article: a
  // leftover open tab would mount the new page with the sheet already open.
  // Keyed on the SLUG alone — the effect above also re-fires when
  // `enabledTabs` changes identity (a seeded-detail refetch, the room query
  // resolving), and that must not close a sheet the reader is looking at.
  useEffect(() => {
    setWorksScoreSheetTarget("closed");
  }, [params.slug]);

  // The evidence sheet only exists on the Overview tab; leaving the tab
  // unmounts it WITHOUT closing its state, so returning would reopen it on
  // its own. Closing rides the tab change itself.
  const changeActiveTab = (tab: ArticlePageTabName) => {
    setActiveTab(tab);
    setWorksScoreSheetTarget("closed");
  };

  return {
    activeTab,
    isBackgroundExpanded,
    isMethodologyExpanded,
    isLimitationsExpanded,
    isObjectivesExpanded,
    isResultsExpanded,
    isConclusionExpanded,
    isPlaylistsPanelVisible,
    worksScoreSheetTarget,
    isArticleReadMarkerOnScreen,
    articleReadMarkerRef,
    contentTopRef,
    setActiveTab: changeActiveTab,
    setBackgroundExpanded,
    setMethodologyExpanded,
    setLimitationsExpanded,
    setObjectivesExpanded,
    setResultsExpanded,
    setConclusionExpanded,
    setPlaylistsPanelVisible,
    setWorksScoreSheetTarget,
    enabledTabs,
    canDisplayContent,
    isAllowedArticle,
    hasDigestContent,
    hasShowableAbstract,
  };
}
